import { generateClient } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getPublicCompany, getPublicUser } from '../graphQL/publicQueries';

const TTL = 5 * 60 * 1000;
const MAX_CACHE_SIZE = 500;
const BATCH_CONCURRENCY = 10;

const cache = new Map();

let _client = null;
const getClient = () => {
  if (!_client) _client = generateClient();
  return _client;
};

const resolveAuthMode = async () => {
  try {
    const session = await fetchAuthSession();
    return session?.tokens?.idToken ? 'userPool' : 'iam';
  } catch {
    return 'iam';
  }
};

const isValid = (entry) => entry && Date.now() - entry.timestamp < TTL;

const getCached = (type, id) => {
  const key = `${type}:${id}`;
  const entry = cache.get(key);
  return isValid(entry) ? entry.data : null;
};

const setCache = (type, id, data) => {
  if (cache.size >= MAX_CACHE_SIZE) {
    const firstKey = cache.keys().next().value;
    cache.delete(firstKey);
  }
  cache.set(`${type}:${id}`, { data, timestamp: Date.now() });
};

const batchFetch = async (ids, queryFn, type, authMode) => {
  const client = getClient();
  const unique = [...new Set(ids)];
  const result = {};
  const missing = [];
  const mode = authMode || await resolveAuthMode();
  const cacheType = `${type}:${mode}`;

  unique.forEach((id) => {
    const cached = getCached(cacheType, id);
    if (cached) {
      result[id] = cached;
    } else {
      missing.push(id);
    }
  });

  if (missing.length > 0) {
    for (let i = 0; i < missing.length; i += BATCH_CONCURRENCY) {
      const batch = missing.slice(i, i + BATCH_CONCURRENCY);
      const fetches = batch.map(async (id) => {
        try {
          const { data } = await client.graphql({
            query: queryFn,
            variables: { id },
            authMode: mode,
          });
          const key = type === 'company' ? 'getCompany' : 'getUser';
          if (data[key]) {
            setCache(cacheType, id, data[key]);
            result[id] = data[key];
          }
        } catch (error) {
          const key = type === 'company' ? 'getCompany' : 'getUser';
          const item = error?.data?.[key];
          if (item) {
            setCache(cacheType, id, item);
            result[id] = item;
          } else {
            result[id] = null;
          }
        }
      });
      await Promise.all(fetches);
    }
  }

  return result;
};

export const fetchCompanies = (ids, authMode) => batchFetch(ids, getPublicCompany, 'company', authMode);

export const fetchUsers = (ids, authMode) => batchFetch(ids, getPublicUser, 'user', authMode);

export const invalidateEntity = (type, id) => {
  cache.delete(`${type}:userPool:${id}`);
  cache.delete(`${type}:iam:${id}`);
};

export const clearCache = () => {
  cache.clear();
};
